import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from "react";
import { scaleTime } from "d3-scale";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  Box,
  Button,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Menu,
  MenuItem,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { alpha } from "@mui/material/styles";
import { buildLayoutPayload } from "./normalizeTimelineViewModel";
import { computeDefaultViewDomain } from "./chronos/chronosDefaultZoom";
import {
  generateAxisTicks,
  formatAxisTickLabel,
  getScaleMode,
  shouldShowStickyYear,
  stickyYearLabelFromViewStart,
} from "./chronos/chronosTimeAxis";
import {
  collectBoundaryEventIds,
  clampViewWindowToExtent,
  computeDataExtentMsWithPadding,
  estimateGanttLabelColumnWidth,
  itemInLane,
  itemKey,
  laneContentHeight,
  packGanttItemsInLane,
  ganttRowCount,
  splitTitleLines,
  truncateEventName,
} from "./chronos/chronosEventLayout";
import { formatEventDateLine, formatPeriodRangeLine } from "./chronos/chronosItemLabels";
import ChronosMinimap from "./ChronosMinimap";
import {
  AXIS_BAND_HEIGHT,
  AXIS_LINE_COLOR,
  AXIS_TICK_LABEL_COLOR,
  CHRONOS_CANVAS_BG,
  CHRONOS_EVENT_BORDER,
  CHRONOS_EVENT_COLOR,
  DOT_COLUMN_PX,
  DOT_RADIUS,
  ERA_LABEL_BAND,
  ERA_LANE_DIVIDER,
  ERA_LANE_FILL,
  ERA_LANE_STROKE,
  ERA_TITLE_FONT_PX,
  ERA_TITLE_HALO_STROKE,
  ERA_TITLE_HALO_STROKE_WIDTH,
  estimateEraTitleWidthPx,
  GRID_LINE_COLOR,
  GRID_LINE_MAJOR,
  LANE_MIN_HEIGHT,
  LANE_SEPARATOR_COLOR,
  LANE_SIDEBAR_WIDTH,
  LANE_TOP_PAD,
  PERIOD_BAR_HEIGHT,
  PERIOD_FILL,
  PERIOD_FILL_STROKE,
  ROW_HEIGHT,
} from "./chronos/chronosConstants";
import { captureChronosCardToPng } from "./chronosExportPng";

/** Vertical-only drag between lanes. */
function restrictVerticalTransform({ transform }) {
  return { ...transform, x: 0 };
}

function DraggableSvgItem({
  id,
  disabled,
  itemType,
  itemId,
  children,
  svgPointerEvents,
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id,
      disabled,
      data: { type: itemType, itemId },
    });
  const t = transform ? `translate(0, ${transform.y}px)` : undefined;
  return (
    <g
      ref={setNodeRef}
      transform={t}
      style={{
        cursor: disabled ? "default" : isDragging ? "grabbing" : "grab",
        opacity: disabled ? 1 : isDragging ? 0.38 : 1,
        pointerEvents: svgPointerEvents,
      }}
      {...listeners}
      {...attributes}
    >
      {children}
    </g>
  );
}

function LaneDropZone({ laneId, width, height, activeDrag }) {
  const theme = useTheme();
  const { setNodeRef, isOver } = useDroppable({ id: `lane:${String(laneId)}` });
  const pe = activeDrag ? "auto" : "none";
  return (
    <foreignObject x={0} y={0} width={width} height={height}>
      <div
        ref={setNodeRef}
        xmlns="http://www.w3.org/2000/xhtml"
        style={{
          width: "100%",
          height: "100%",
          margin: 0,
          padding: 0,
          boxSizing: "border-box",
          pointerEvents: pe,
          backgroundColor: isOver
            ? alpha(theme.palette.primary.main, 0.18)
            : "transparent",
          transition: "background-color 0.12s ease",
        }}
      />
    </foreignObject>
  );
}

const ChronosTimelineView = forwardRef(function ChronosTimelineView(
  {
    viewModel,
    editMode,
    isOwner,
    dateMode,
    onDateModeChange,
    onItemClick,
    onSaveLayout,
    savingLayout,
    embedMode = false,
    chartCardRef,
  },
  ref
) {
  const theme = useTheme();
  /** Dark canvas; MUI default theme has no ThemeProvider so palette background stays #fff. */
  const canvasBg = CHRONOS_CANVAS_BG;

  const [layoutState, setLayoutState] = useState(null);
  useEffect(() => {
    if (!viewModel) return;
    setLayoutState({
      lanes: viewModel.lanes.map((l) => ({ ...l })),
      items: viewModel.items.map((it) => ({ ...it })),
    });
  }, [viewModel]);

  const [viewStart, setViewStart] = useState(null);
  const [viewEnd, setViewEnd] = useState(null);
  const [exportWide, setExportWide] = useState(null);
  const cardElRef = useRef(null);
  const pendingExportRef = useRef(null);
  const exportWideBlockingResizeRef = useRef(false);
  const [chartAreaW, setChartAreaW] = useState(800);
  const chartAreaRef = useRef(null);
  const panRef = useRef({ active: false, lastX: 0, view0: 0, view1: 0 });
  const [dragActive, setDragActive] = useState(false);
  const [dragPreview, setDragPreview] = useState(null);
  const [hoverItemKey, setHoverItemKey] = useState(null);
  const [laneMenu, setLaneMenu] = useState({ anchor: null, laneId: null });

  const innerChartW = Math.max(320, Math.round(chartAreaW));

  useEffect(() => {
    exportWideBlockingResizeRef.current = exportWide != null;
  }, [exportWide]);

  const layoutViewStart = exportWide?.viewStart ?? viewStart;
  const layoutViewEnd = exportWide?.viewEnd ?? viewEnd;
  const layoutInnerW =
    exportWide != null ? Math.max(320, Math.round(exportWide.width)) : innerChartW;

  const lastDomainInitIdRef = useRef(null);
  const domainInitWidthRef = useRef(0);
  useEffect(() => {
    if (!layoutState || !viewModel?.id) return;
    const tid = String(viewModel.id);
    const w = innerChartW;
    if (w < 200) return;
    const [a, b] = computeDefaultViewDomain(
      layoutState.items,
      layoutState.lanes,
      w,
      dateMode
    );
    if (lastDomainInitIdRef.current !== tid) {
      lastDomainInitIdRef.current = tid;
      domainInitWidthRef.current = w;
      setViewStart(a);
      setViewEnd(b);
      return;
    }
    const w0 = domainInitWidthRef.current;
    if (w0 > 0 && w >= w0 * 1.2) {
      domainInitWidthRef.current = w;
      setViewStart(a);
      setViewEnd(b);
    }
  }, [viewModel?.id, layoutState, innerChartW, dateMode]);

  const lanesSorted = useMemo(() => {
    if (!layoutState) return [];
    return [...layoutState.lanes].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }, [layoutState]);

  const showLaneChrome = useMemo(() => {
    if (lanesSorted.length !== 1) return true;
    const n = (lanesSorted[0].name || "").trim();
    return n.length > 0;
  }, [lanesSorted]);

  const sidebarW = showLaneChrome ? LANE_SIDEBAR_WIDTH : 0;

  const chartReady =
    layoutState != null && viewStart != null && viewEnd != null;

  useLayoutEffect(() => {
    const el = chartAreaRef.current;
    if (!el || !chartReady) return undefined;
    let rafId = 0;
    const WIDTH_EPS_PX = 4;
    const measure = () => {
      if (exportWideBlockingResizeRef.current) return;
      const w = el.clientWidth;
      if (w <= 0) return;
      const rounded = Math.round(w);
      setChartAreaW((prev) => {
        const pr = Math.round(prev);
        if (pr > 0 && Math.abs(pr - rounded) < WIDTH_EPS_PX) return prev;
        return rounded;
      });
    };
    measure();
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(measure);
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [chartReady, showLaneChrome, viewModel?.id]);

  const xScale = useMemo(() => {
    if (!layoutViewStart || !layoutViewEnd) return null;
    return scaleTime()
      .domain([layoutViewStart, layoutViewEnd])
      .range([0, layoutInnerW]);
  }, [layoutViewStart, layoutViewEnd, layoutInnerW]);

  const boundaryIds = useMemo(
    () => collectBoundaryEventIds(layoutState?.items || []),
    [layoutState?.items]
  );

  const dataExtent = useMemo(() => {
    if (!layoutState || !viewStart || !viewEnd) return null;
    const { minT, maxT } = computeDataExtentMsWithPadding(
      layoutState.items,
      viewStart,
      viewEnd
    );
    return { minT, maxT };
  }, [layoutState, viewStart, viewEnd]);

  const { laneLayouts, contentBottom } = useMemo(() => {
    if (!layoutState || !layoutViewStart || !layoutViewEnd) {
      return {
        laneLayouts: [],
        contentBottom: AXIS_BAND_HEIGHT,
      };
    }

    const out = [];
    let y = AXIS_BAND_HEIGHT;
    for (const lane of lanesSorted) {
      const items = layoutState.items.filter((it) =>
        itemInLane(it, lane, lanesSorted)
      );
      const eras = items
        .filter((it) => it.type === "period" && it.isEra && it.startDate)
        .sort(
          (a, b) =>
            a.startDate.getTime() - b.startDate.getTime()
        );
      const packed = packGanttItemsInLane(
        items,
        boundaryIds,
        layoutViewStart,
        layoutViewEnd,
        layoutInnerW,
        dateMode
      );
      const rowCount = ganttRowCount(packed);
      const hasEra = eras.length > 0;
      const h = laneContentHeight(rowCount, hasEra);
      out.push({
        lane,
        laneTop: y,
        laneHeight: h,
        packed,
        eras,
        hasEraInLane: hasEra,
      });
      y += h;
    }
    return {
      laneLayouts: out,
      contentBottom: y,
    };
  }, [
    layoutState,
    lanesSorted,
    boundaryIds,
    layoutInnerW,
    layoutViewStart,
    layoutViewEnd,
    dateMode,
  ]);

  const domainMs =
    layoutViewStart && layoutViewEnd
      ? layoutViewEnd.getTime() - layoutViewStart.getTime()
      : 864e5;
  const scaleMode = getScaleMode(domainMs);
  const { ticks, majorEvery } =
    layoutViewStart && layoutViewEnd
      ? generateAxisTicks(
          layoutViewStart,
          layoutViewEnd,
          scaleMode,
          dateMode
        )
      : { ticks: [], majorEvery: () => false };

  const showSticky =
    scaleMode === "month" &&
    shouldShowStickyYear(layoutViewStart, layoutViewEnd) &&
    layoutViewStart &&
    layoutViewEnd;

  useImperativeHandle(
    ref,
    () => ({
      exportWideFullSpanPng: ({ maxWidthPx = 32000 } = {}) =>
        new Promise((resolve, reject) => {
          if (!layoutState || !viewStart || !viewEnd || innerChartW < 8) {
            reject(new Error("Graphique indisponible."));
            return;
          }
          const { minT, maxT } = computeDataExtentMsWithPadding(
            layoutState.items,
            viewStart,
            viewEnd
          );
          const spanView = viewEnd.getTime() - viewStart.getTime();
          const spanFull = Math.max(1, maxT - minT);
          const exportW = Math.ceil((spanFull / spanView) * innerChartW);
          if (exportW > maxWidthPx) {
            reject(
              new Error(
                `Largeur d'export ${exportW}px — limite ${maxWidthPx}px. Élargissez la fenêtre temporelle (zoom arrière) pour réduire la largeur du fichier.`
              )
            );
            return;
          }
          pendingExportRef.current = { resolve, reject };
          setExportWide({
            viewStart: new Date(minT),
            viewEnd: new Date(maxT),
            width: exportW,
          });
        }),
    }),
    [layoutState, viewStart, viewEnd, innerChartW]
  );

  useLayoutEffect(() => {
    if (!exportWide || !pendingExportRef.current) return undefined;
    const pending = pendingExportRef.current;
    const tid = window.setTimeout(async () => {
      try {
        await new Promise((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        });
        const el = cardElRef.current;
        if (!el) throw new Error("Élément introuvable.");
        const dataUrl = await captureChronosCardToPng(el, {
          backgroundColor: CHRONOS_CANVAS_BG,
          skipFonts: true,
        });
        pending.resolve(dataUrl);
      } catch (e) {
        pending.reject(e);
      } finally {
        pendingExportRef.current = null;
        setExportWide(null);
      }
    }, 0);
    return () => clearTimeout(tid);
  }, [exportWide]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const persistLayout = useCallback(
    (nextLanes, nextItems) => {
      if (!onSaveLayout) return;
      onSaveLayout(buildLayoutPayload(nextLanes, nextItems));
    },
    [onSaveLayout]
  );

  const moveLane = useCallback(
    (laneId, dir) => {
      if (!editMode || !isOwner || !layoutState) return;
      const lanes = [...lanesSorted].map((l) => ({ ...l }));
      const idx = lanes.findIndex((l) => String(l.id) === String(laneId));
      if (idx < 0) return;
      const j = idx + dir;
      if (j < 0 || j >= lanes.length) return;
      [lanes[idx], lanes[j]] = [lanes[j], lanes[idx]];
      lanes.forEach((l, i) => {
        l.sortOrder = i;
      });
      setLayoutState((s) => ({ ...s, lanes }));
      persistLayout(lanes, layoutState.items);
    },
    [editMode, isOwner, layoutState, lanesSorted, persistLayout]
  );

  const renameLane = useCallback(
    (laneId, name) => {
      if (!editMode || !isOwner || !layoutState) return;
      const lanes = layoutState.lanes.map((l) =>
        String(l.id) === String(laneId) ? { ...l, name } : l
      );
      setLayoutState((s) => ({ ...s, lanes }));
      persistLayout(lanes, layoutState.items);
    },
    [editMode, isOwner, layoutState, persistLayout]
  );

  const addLane = useCallback(() => {
    if (!editMode || !isOwner || !layoutState) return;
    const maxOrder = Math.max(
      0,
      ...layoutState.lanes.map((l) => l.sortOrder ?? 0)
    );
    const lanes = [
      ...layoutState.lanes.map((l) => ({ ...l })),
      {
        id: undefined,
        name: `Voie ${layoutState.lanes.length + 1}`,
        sortOrder: maxOrder + 1,
      },
    ];
    setLayoutState((s) => ({ ...s, lanes }));
    persistLayout(lanes, layoutState.items);
  }, [editMode, isOwner, layoutState, persistLayout]);

  const deleteLane = useCallback(
    (laneId) => {
      if (!editMode || !isOwner || !layoutState) return;
      const remaining = layoutState.lanes.filter(
        (l) => String(l.id) !== String(laneId)
      );
      if (!remaining.length) return;
      const firstId = remaining[0].id;
      const items = layoutState.items.map((it) =>
        String(it.laneId) === String(laneId) ? { ...it, laneId: firstId } : it
      );
      const lanes = remaining.map((l, i) => ({ ...l, sortOrder: i }));
      setLayoutState({ lanes, items });
      persistLayout(lanes, items);
      setLaneMenu({ anchor: null, laneId: null });
    },
    [editMode, isOwner, layoutState, persistLayout]
  );

  const assignItemToLane = useCallback(
    (itemType, itemId, newLaneId) => {
      if (!editMode || !isOwner || !layoutState) return;
      const items = layoutState.items.map((it) =>
        it.type === itemType && String(it.id) === String(itemId)
          ? { ...it, laneId: newLaneId }
          : it
      );
      setLayoutState((s) => ({ ...s, items }));
      persistLayout(layoutState.lanes, items);
    },
    [editMode, isOwner, layoutState, persistLayout]
  );

  const handleDragEnd = useCallback(
    (event) => {
      setDragActive(false);
      setDragPreview(null);
      const { active, over } = event;
      if (!over || !active?.data?.current) return;
      const laneId = String(over.id).startsWith("lane:")
        ? String(over.id).slice(5)
        : null;
      if (!laneId) return;
      const { type, itemId } = active.data.current;
      assignItemToLane(type, itemId, laneId);
    },
    [assignItemToLane]
  );

  const viewWindowRef = useRef({ start: null, end: null });
  useEffect(() => {
    viewWindowRef.current = { start: viewStart, end: viewEnd };
  }, [viewStart, viewEnd]);

  const scrollExtentRef = useRef(null);
  useEffect(() => {
    scrollExtentRef.current = dataExtent;
  }, [dataExtent]);

  const chartWRef = useRef(innerChartW);
  useEffect(() => {
    chartWRef.current = innerChartW;
  }, [innerChartW]);

  useLayoutEffect(() => {
    if (!viewStart || !viewEnd) return;
    const el = chartAreaRef.current;
    if (!el) return;
    const onWheelNative = (e) => {
      const { start, end } = viewWindowRef.current;
      if (!start || !end) return;
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      e.stopPropagation();
      const rect = el.getBoundingClientRect();
      const rw = rect.width > 0 ? rect.width : Math.max(320, chartWRef.current);
      const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rw));
      const t0 = start.getTime();
      const t1 = end.getTime();
      const span = t1 - t0;
      const tc = t0 + frac * span;
      const zoomOut = e.deltaY > 0;
      const k = zoomOut ? 1.1 : 1 / 1.1;
      const na = tc + (t0 - tc) * k;
      const nb = tc + (t1 - tc) * k;
      const ext = scrollExtentRef.current;
      if (ext) {
        const c = clampViewWindowToExtent(
          new Date(na),
          new Date(nb),
          ext.minT,
          ext.maxT
        );
        setViewStart(c.start);
        setViewEnd(c.end);
      } else {
        setViewStart(new Date(na));
        setViewEnd(new Date(nb));
      }
    };
    el.addEventListener("wheel", onWheelNative, { passive: false });
    return () => el.removeEventListener("wheel", onWheelNative);
  }, [viewStart, viewEnd, innerChartW]);

  const onPointerDownPan = useCallback(
    (e) => {
      if (e.target.closest("[data-timeline-interactive]")) return;
      if (e.button !== 1) return;
      if (!viewStart || !viewEnd) return;
      e.preventDefault();
      panRef.current = {
        active: true,
        lastX: e.clientX,
        view0: viewStart.getTime(),
        view1: viewEnd.getTime(),
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [viewStart, viewEnd]
  );

  const onPointerMovePan = useCallback(
    (e) => {
      const p = panRef.current;
      if (!p.active || !innerChartW) return;
      const dx = e.clientX - p.lastX;
      p.lastX = e.clientX;
      const span = p.view1 - p.view0;
      const dMs = (-dx / innerChartW) * span;
      const ext = scrollExtentRef.current;
      if (ext) {
        const c = clampViewWindowToExtent(
          new Date(p.view0 + dMs),
          new Date(p.view1 + dMs),
          ext.minT,
          ext.maxT
        );
        const ns = c.start.getTime();
        const ne = c.end.getTime();
        setViewStart(c.start);
        setViewEnd(c.end);
        p.view0 = ns;
        p.view1 = ne;
      } else {
        setViewStart(new Date(p.view0 + dMs));
        setViewEnd(new Date(p.view1 + dMs));
        p.view0 += dMs;
        p.view1 += dMs;
      }
    },
    [innerChartW]
  );

  const onPointerUpPan = useCallback((e) => {
    panRef.current.active = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  const onPanByDataMs = useCallback((deltaMs) => {
    const { start, end } = viewWindowRef.current;
    if (!start || !end) return;
    const ext = scrollExtentRef.current;
    if (ext) {
      const c = clampViewWindowToExtent(
        new Date(start.getTime() + deltaMs),
        new Date(end.getTime() + deltaMs),
        ext.minT,
        ext.maxT
      );
      setViewStart(c.start);
      setViewEnd(c.end);
    } else {
      setViewStart(new Date(start.getTime() + deltaMs));
      setViewEnd(new Date(end.getTime() + deltaMs));
    }
  }, []);

  /** Minimap thumb: absolute start time so the thumb stays under the cursor (delta + clamp was lagging). */
  const onSeekViewStartMs = useCallback((targetMinTMs) => {
    const { start, end } = viewWindowRef.current;
    if (!start || !end) return;
    const span = end.getTime() - start.getTime();
    const ext = scrollExtentRef.current;
    if (ext) {
      const c = clampViewWindowToExtent(
        new Date(targetMinTMs),
        new Date(targetMinTMs + span),
        ext.minT,
        ext.maxT
      );
      setViewStart(c.start);
      setViewEnd(c.end);
    } else {
      setViewStart(new Date(targetMinTMs));
      setViewEnd(new Date(targetMinTMs + span));
    }
  }, []);

  const svgHeight = Math.max(AXIS_BAND_HEIGHT + LANE_MIN_HEIGHT, contentBottom);

  const rowTopInLane = (laneTop, hasEra, row) =>
    laneTop +
    (hasEra ? ERA_LABEL_BAND : 0) +
    LANE_TOP_PAD +
    row * ROW_HEIGHT;

  if (!layoutState || !viewStart || !viewEnd || !xScale) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="text.secondary">Chargement du canevas…</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "none",
        minWidth: 0,
        alignSelf: "stretch",
        mb: 2,
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            rowGap: 0.5,
          }}
        >
          <ToggleButtonGroup
            size="small"
            value={dateMode}
            exclusive
            onChange={(_, v) => v && onDateModeChange(v)}
          >
            <ToggleButton value="irl">IRL</ToggleButton>
            <ToggleButton value="erache">Erache</ToggleButton>
          </ToggleButtonGroup>
          {!embedMode && (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1.35, maxWidth: { xs: "100%", sm: 420 } }}
            >
              Ctrl + molette (⌘ + molette sur Mac) : zoom. Molette du milieu : déplacer
              horizontalement.
            </Typography>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {savingLayout ? "Enregistrement…" : ""}
        </Typography>
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictVerticalTransform]}
        onDragStart={(e) => {
          setDragActive(true);
          const d = e.active?.data?.current;
          if (d?.type != null && d?.itemId != null && layoutState?.items) {
            const it = layoutState.items.find(
              (x) => x.type === d.type && String(x.id) === String(d.itemId)
            );
            setDragPreview({
              id: String(e.active.id),
              kind: d.type,
              name: (it && it.name) || "",
            });
          } else {
            setDragPreview(null);
          }
        }}
        onDragCancel={() => {
          setDragActive(false);
          setDragPreview(null);
        }}
        onDragEnd={handleDragEnd}
      >
        <Box
          ref={(node) => {
            cardElRef.current = node;
            if (typeof chartCardRef === "function") chartCardRef(node);
            else if (chartCardRef) chartCardRef.current = node;
          }}
          sx={{
            display: "flex",
            width: "100%",
            maxWidth: "none",
            minWidth: 0,
            alignSelf: "stretch",
            minHeight: 200,
            borderRadius: 1,
            overflow: exportWide ? "visible" : "hidden",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: canvasBg,
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flex: 1,
              minHeight: 0,
              width: "100%",
              ...(exportWide
                ? {
                    minWidth: sidebarW + layoutInnerW,
                    overflow: "visible",
                  }
                : {}),
            }}
          >
            {showLaneChrome && (
              <Box
                sx={{
                  width: sidebarW,
                  flexShrink: 0,
                  borderRight: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  flexDirection: "column",
                  bgcolor: alpha(CHRONOS_CANVAS_BG, 0.5),
                  // Match SVG: lanes start below the time-axis band (y = AXIS_BAND_HEIGHT).
                  pt: `${AXIS_BAND_HEIGHT}px`,
                  pb: 0.5,
                }}
              >
                {laneLayouts.map(({ lane, laneHeight }) => (
                  <Box
                    key={String(lane.id)}
                    sx={{
                      height: laneHeight,
                      minHeight: laneHeight,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      px: 0.5,
                      borderBottom: `1px solid ${LANE_SEPARATOR_COLOR}`,
                      position: "relative",
                      "&:hover .lane-menu-btn": { opacity: 1 },
                    }}
                  >
                    {editMode && isOwner ? (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.25,
                            flexWrap: "wrap",
                          }}
                        >
                          <IconButton
                            size="small"
                            className="lane-menu-btn"
                            sx={{ opacity: 0, transition: "opacity 0.15s" }}
                            aria-label="Menu voie"
                            onClick={(e) =>
                              setLaneMenu({
                                anchor: e.currentTarget,
                                laneId: lane.id,
                              })
                            }
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => moveLane(lane.id, -1)}
                            aria-label="Monter"
                          >
                            <KeyboardArrowUpIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => moveLane(lane.id, 1)}
                            aria-label="Descendre"
                          >
                            <KeyboardArrowDownIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <TextField
                          size="small"
                          fullWidth
                          value={lane.name || ""}
                          onChange={(e) => renameLane(lane.id, e.target.value)}
                          sx={{ mt: 0.5 }}
                        />
                      </>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          px: 0.5,
                          wordBreak: "break-word",
                          color: AXIS_TICK_LABEL_COLOR,
                        }}
                      >
                        {lane.name || "—"}
                      </Typography>
                    )}
                  </Box>
                ))}
                {editMode && isOwner && (
                  <Box sx={{ p: 0.5 }}>
                    <Button size="small" startIcon={<AddIcon />} onClick={addLane}>
                      Voie
                    </Button>
                  </Box>
                )}
                <Menu
                  anchorEl={laneMenu.anchor}
                  open={Boolean(laneMenu.anchor)}
                  onClose={() => setLaneMenu({ anchor: null, laneId: null })}
                >
                  <MenuItem
                    onClick={() => {
                      if (laneMenu.laneId) deleteLane(laneMenu.laneId);
                    }}
                    disabled={lanesSorted.length <= 1}
                    sx={{ color: "error.main" }}
                  >
                    Supprimer la voie
                  </MenuItem>
                </Menu>
              </Box>
            )}

            <Box
              ref={chartAreaRef}
              sx={{
                flex: 1,
                minWidth: exportWide ? layoutInnerW : 0,
                position: "relative",
                overflow: exportWide ? "visible" : "hidden",
                display: "flex",
                flexDirection: "column",
                bgcolor: canvasBg,
              }}
            >
              {showSticky && (
                <Typography
                  variant="caption"
                  sx={{
                    position: "absolute",
                    left: 8,
                    top: 6,
                    zIndex: 2,
                    color: AXIS_TICK_LABEL_COLOR,
                    pointerEvents: "none",
                    fontWeight: 600,
                  }}
                >
                  {stickyYearLabelFromViewStart(layoutViewStart, dateMode)}
                </Typography>
              )}
              <Box
                sx={{
                  position: "relative",
                  flex: 1,
                  minHeight: 0,
                  minWidth: exportWide ? layoutInnerW : 0,
                  overflow: exportWide ? "visible" : undefined,
                  bgcolor: canvasBg,
                }}
              >
                <svg
                  width={layoutInnerW}
                  height={svgHeight}
                  style={{
                    display: "block",
                    minWidth: exportWide ? layoutInnerW : 0,
                    backgroundColor: canvasBg,
                  }}
                  onPointerDown={onPointerDownPan}
                  onPointerMove={onPointerMovePan}
                  onPointerUp={onPointerUpPan}
                  onPointerLeave={onPointerUpPan}
                >
                  <rect
                    data-pan-layer="1"
                    x={0}
                    y={0}
                    width={layoutInnerW}
                    height={svgHeight}
                    fill="transparent"
                    style={{ cursor: "default" }}
                  />
                  <rect
                    x={0}
                    y={0}
                    width={layoutInnerW}
                    height={AXIS_BAND_HEIGHT}
                    fill={canvasBg}
                    style={{ pointerEvents: "none" }}
                  />
                  <line
                    x1={0}
                    y1={AXIS_BAND_HEIGHT}
                    x2={layoutInnerW}
                    y2={AXIS_BAND_HEIGHT}
                    stroke={AXIS_LINE_COLOR}
                    strokeWidth={1}
                    pointerEvents="none"
                  />

                  <g style={{ pointerEvents: "none" }}>
                    {ticks.map((t, i) => {
                      const x = xScale(t);
                      const major = majorEvery(t);
                      return (
                        <line
                          key={`g-${i}`}
                          x1={x}
                          y1={AXIS_BAND_HEIGHT}
                          x2={x}
                          y2={contentBottom}
                          stroke={major ? GRID_LINE_MAJOR : GRID_LINE_COLOR}
                          strokeWidth={major ? 1.2 : 1}
                          opacity={0.85}
                        />
                      );
                    })}
                  </g>

                  {laneLayouts.map(({ laneTop, laneHeight }, li) =>
                    li > 0 ? (
                      <line
                        key={`sep-${li}`}
                        x1={0}
                        y1={laneTop}
                        x2={layoutInnerW}
                        y2={laneTop}
                        stroke={LANE_SEPARATOR_COLOR}
                        strokeWidth={1}
                        pointerEvents="none"
                      />
                    ) : null
                  )}

                  {editMode &&
                    isOwner &&
                    laneLayouts.map(({ lane, laneTop, laneHeight }) => (
                      <g
                        key={`dzg-${lane.id}`}
                        transform={`translate(0,${laneTop})`}
                      >
                        <LaneDropZone
                          laneId={lane.id}
                          width={layoutInnerW}
                          height={laneHeight}
                          activeDrag={dragActive}
                        />
                      </g>
                    ))}

                  {laneLayouts.map(({ lane, laneTop, laneHeight, eras }) => (
                    <g key={`eras-${lane.id}`}>
                      {[...eras]
                        .sort((a, b) => {
                          const av = a?.startDate ? a.startDate.getTime() : 0;
                          const bv = b?.startDate ? b.startDate.getTime() : 0;
                          return av - bv;
                        })
                        .map((it, idx, arr) => {
                        if (!it.startDate) return null;
                        const x0 = xScale(it.startDate);
                        const x1 = it.endDate
                          ? xScale(it.endDate)
                          : layoutInnerW;
                        const w = Math.max(2, x1 - x0);
                        const prev = idx > 0 ? arr[idx - 1] : null;
                        const prevX1 =
                          prev && prev.startDate
                            ? prev.endDate
                              ? xScale(prev.endDate)
                              : layoutInnerW
                            : null;
                        const hasSharedBoundary = prevX1 != null && x0 <= prevX1 + 1;
                        const eraName = truncateEventName(it.name || "");
                        const labelW = estimateEraTitleWidthPx(eraName);
                        const pad = 6;
                        const viewLeftPad = pad;
                        const stickyLeft = Math.max(x0 + pad, viewLeftPad);
                        const maxLeft = x1 - labelW - pad;
                        const labelX = Math.min(stickyLeft, maxLeft);
                        const clipId = `era-clip-${String(lane.id)}-${itemKey(it)}`.replace(
                          /[^a-zA-Z0-9_-]/g,
                          "-"
                        );
                        const canDragEra = editMode && isOwner;
                        const eraPe =
                          !canDragEra ? "auto" : dragActive ? "none" : "auto";
                        const eraBody = (
                          <g key={`era-${itemKey(it)}`}>
                            <defs>
                              <clipPath id={clipId}>
                                <rect
                                  x={x0}
                                  y={laneTop}
                                  width={w}
                                  height={laneHeight}
                                />
                              </clipPath>
                            </defs>
                            <rect
                              data-timeline-interactive="1"
                              x={x0}
                              y={laneTop}
                              width={w}
                              height={laneHeight}
                              fill={ERA_LANE_FILL}
                              stroke={ERA_LANE_STROKE}
                              strokeWidth={2}
                              rx={2}
                              pointerEvents={eraPe}
                              style={{ cursor: canDragEra ? "grab" : "pointer" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                onItemClick({
                                  type: "period",
                                  id: it.id,
                                });
                              }}
                            />
                            {hasSharedBoundary ? (
                              <line
                                data-timeline-interactive="1"
                                x1={x0}
                                y1={laneTop + 1}
                                x2={x0}
                                y2={laneTop + laneHeight - 1}
                                stroke={ERA_LANE_DIVIDER}
                                strokeWidth={1.5}
                                pointerEvents={eraPe}
                              />
                            ) : null}
                            <g clipPath={`url(#${clipId})`}>
                              <rect
                                data-timeline-interactive="1"
                                x={Math.max(x0, labelX - 4)}
                                y={laneTop}
                                width={Math.min(
                                  labelW + 8,
                                  Math.max(0, x0 + w - Math.max(x0, labelX - 4))
                                )}
                                height={Math.min(ERA_LABEL_BAND, laneHeight)}
                                fill="transparent"
                                style={{ cursor: "pointer" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onItemClick({
                                    type: "period",
                                    id: it.id,
                                  });
                                }}
                              />
                              <text
                                x={labelX}
                                y={laneTop + 18}
                                fill="#ffffff"
                                stroke={ERA_TITLE_HALO_STROKE}
                                strokeWidth={ERA_TITLE_HALO_STROKE_WIDTH}
                                paintOrder="stroke fill"
                                strokeLinejoin="round"
                                fontSize={ERA_TITLE_FONT_PX}
                                fontWeight={700}
                                fontFamily={theme.typography.fontFamily}
                                pointerEvents="none"
                              >
                                {eraName}
                              </text>
                            </g>
                          </g>
                        );
                        if (!canDragEra) return eraBody;
                        return (
                          <DraggableSvgItem
                            key={`drag-era-${itemKey(it)}`}
                            id={`item:${itemKey(it)}`}
                            disabled={false}
                            itemType="period"
                            itemId={it.id}
                            svgPointerEvents={dragActive ? "none" : undefined}
                          >
                            {eraBody}
                          </DraggableSvgItem>
                        );
                      })}
                    </g>
                  ))}

                  {laneLayouts.map(
                    ({
                      lane,
                      laneTop,
                      packed,
                      hasEraInLane,
                    }) =>
                      packed.map(({ it, row }) => {
                        const ikey = itemKey(it);
                        const rt = rowTopInLane(laneTop, hasEraInLane, row);
                        const rowCenter = rt + ROW_HEIGHT / 2;
                        const canDrag = editMode && isOwner;
                        const pe =
                          !canDrag ? "none" : dragActive ? "none" : "auto";
                        const hov = hoverItemKey === ikey && canDrag;

                        if (it.type === "event" && it.startDate) {
                          const cx = Math.round(xScale(it.startDate));
                          const label = truncateEventName(it.name);
                          const titleLines = splitTitleLines(label);
                          const dateLine = formatEventDateLine(
                            it.startDate,
                            dateMode
                          );
                          const eventLabelW = estimateGanttLabelColumnWidth(
                            it.name || "",
                            dateLine
                          );
                          const spaceRight = layoutInnerW - cx - DOT_COLUMN_PX;
                          const eventFoW = Math.max(
                            40,
                            Math.min(eventLabelW + 8, Math.max(0, spaceRight))
                          );
                          const fo = (
                            <g
                              data-timeline-interactive="1"
                              onMouseEnter={() => setHoverItemKey(ikey)}
                              onMouseLeave={() => setHoverItemKey(null)}
                              onClick={(e) => {
                                e.stopPropagation();
                                onItemClick({ type: it.type, id: it.id });
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              <circle
                                cx={cx}
                                cy={rowCenter}
                                r={DOT_RADIUS}
                                fill={CHRONOS_EVENT_COLOR}
                                stroke={hov ? "#fff" : CHRONOS_EVENT_BORDER}
                                strokeWidth={hov ? 2 : 1}
                                pointerEvents={pe}
                              />
                              <foreignObject
                                x={cx + DOT_COLUMN_PX}
                                y={rt}
                                width={eventFoW}
                                height={ROW_HEIGHT}
                              >
                                <div
                                  xmlns="http://www.w3.org/2000/xhtml"
                                  style={{
                                    boxSizing: "border-box",
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    padding: "2px 4px",
                                    fontFamily: theme.typography.fontFamily,
                                    pointerEvents: pe,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: "2px",
                                      justifyContent: "center",
                                    }}
                                  >
                                    {titleLines.map((line, idx) => (
                                      <div
                                        key={idx}
                                        style={{
                                          color: "#fff",
                                          fontSize: 13,
                                          fontWeight: 600,
                                          lineHeight: 1.25,
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                        }}
                                      >
                                        {line}
                                      </div>
                                    ))}
                                  </div>
                                  <div
                                    style={{
                                      color: "rgba(255,255,255,0.65)",
                                      fontSize: 11,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {dateLine}
                                  </div>
                                </div>
                              </foreignObject>
                            </g>
                          );
                          if (!canDrag) return <g key={ikey}>{fo}</g>;
                          return (
                            <DraggableSvgItem
                              key={ikey}
                              id={`item:${ikey}`}
                              disabled={false}
                              itemType={it.type}
                              itemId={it.id}
                              svgPointerEvents={dragActive ? "none" : undefined}
                            >
                              {fo}
                            </DraggableSvgItem>
                          );
                        }

                        if (it.type === "period" && !it.isEra && it.startDate) {
                          const x0 = xScale(it.startDate);
                          const x1 = it.endDate
                            ? xScale(it.endDate)
                            : layoutInnerW;
                          const wBar = Math.max(2, x1 - x0);
                          const periodTitleLines = splitTitleLines(
                            truncateEventName(it.name || "")
                          );
                          const dateStr = formatPeriodRangeLine(
                            it.startDate,
                            it.endDate,
                            dateMode
                          );
                          const textW = estimateGanttLabelColumnWidth(
                            it.name || "",
                            dateStr
                          );
                          const labelPad = 6;
                          const labelW = textW;
                          const minLeft = x0 + labelPad;
                          const maxLeft = x1 - labelW - labelPad;
                          const idealLeft = (x0 + x1 - labelW) / 2;
                          let labelLeft;
                          if (maxLeft >= minLeft) {
                            labelLeft = Math.max(
                              minLeft,
                              Math.min(idealLeft, maxLeft)
                            );
                          } else {
                            labelLeft = minLeft;
                          }
                          const foW = Math.max(
                            0,
                            Math.min(textW + 8, Math.max(0, x1 - labelLeft - labelPad))
                          );
                          const clipId = `period-clip-${String(lane.id)}-${itemKey(it)}`.replace(
                            /[^a-zA-Z0-9_-]/g,
                            "-"
                          );
                          const barH = PERIOD_BAR_HEIGHT;
                          const barY = rt + (ROW_HEIGHT - barH) / 2;

                          const body = (
                            <g
                              data-timeline-interactive="1"
                              onMouseEnter={() => setHoverItemKey(ikey)}
                              onMouseLeave={() => setHoverItemKey(null)}
                              onClick={(e) => {
                                e.stopPropagation();
                                onItemClick({ type: it.type, id: it.id });
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              <rect
                                x={x0}
                                y={barY}
                                width={wBar}
                                height={barH}
                                rx={6}
                                fill={PERIOD_FILL}
                                stroke={hov ? "#fff" : PERIOD_FILL_STROKE}
                                strokeWidth={hov ? 2 : 1}
                                pointerEvents={pe}
                              />
                              {foW > 0 && (
                                <>
                                  <defs>
                                    <clipPath id={clipId}>
                                      <rect
                                        x={x0}
                                        y={rt}
                                        width={wBar}
                                        height={ROW_HEIGHT}
                                      />
                                    </clipPath>
                                  </defs>
                                  <g clipPath={`url(#${clipId})`}>
                                    <foreignObject
                                      x={labelLeft}
                                      y={rt}
                                      width={foW}
                                      height={ROW_HEIGHT}
                                    >
                                      <div
                                        xmlns="http://www.w3.org/2000/xhtml"
                                        style={{
                                          boxSizing: "border-box",
                                          height: "100%",
                                          display: "flex",
                                          flexDirection: "column",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          padding: "0 6px",
                                          textAlign: "center",
                                          fontFamily: theme.typography.fontFamily,
                                          pointerEvents: pe,
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            gap: "2px",
                                            width: "100%",
                                          }}
                                        >
                                          {periodTitleLines.map((line, idx) => (
                                            <div
                                              key={idx}
                                              style={{
                                                color: "rgba(255,255,255,0.94)",
                                                fontSize: 13,
                                                fontWeight: 700,
                                                lineHeight: 1.25,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                width: "100%",
                                                textAlign: "center",
                                              }}
                                            >
                                              {line}
                                            </div>
                                          ))}
                                        </div>
                                        <div
                                          style={{
                                            color: "rgba(255,255,255,0.68)",
                                            fontSize: 11,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            width: "100%",
                                          }}
                                        >
                                          {dateStr}
                                        </div>
                                      </div>
                                    </foreignObject>
                                  </g>
                                </>
                              )}
                            </g>
                          );
                          if (!canDrag) return <g key={ikey}>{body}</g>;
                          return (
                            <DraggableSvgItem
                              key={ikey}
                              id={`item:${ikey}`}
                              disabled={false}
                              itemType={it.type}
                              itemId={it.id}
                              svgPointerEvents={dragActive ? "none" : undefined}
                            >
                              {body}
                            </DraggableSvgItem>
                          );
                        }
                        return null;
                      })
                  )}

                  <g pointerEvents="none">
                    {ticks.map((t, i) => {
                      const x = xScale(t);
                      const major = majorEvery(t);
                      return (
                        <g key={`ax-${i}`}>
                          <line
                            x1={x}
                            y1={AXIS_BAND_HEIGHT - 4}
                            x2={x}
                            y2={AXIS_BAND_HEIGHT}
                            stroke={AXIS_TICK_LABEL_COLOR}
                            strokeWidth={major ? 2 : 1}
                          />
                          <text
                            x={x}
                            y={AXIS_BAND_HEIGHT - 10}
                            fill={AXIS_TICK_LABEL_COLOR}
                            fontSize={11}
                            textAnchor="middle"
                          >
                            {formatAxisTickLabel(t, scaleMode, dateMode)}
                          </text>
                        </g>
                      );
                    })}
                  </g>
                </svg>
              </Box>

              {!embedMode && !exportWide && dataExtent && viewStart && viewEnd && (
                <ChronosMinimap
                  dataMinT={dataExtent.minT}
                  dataMaxT={dataExtent.maxT}
                  viewStart={viewStart}
                  viewEnd={viewEnd}
                  onPanByDataMs={onPanByDataMs}
                  onSeekViewStartMs={onSeekViewStartMs}
                />
              )}
            </Box>
          </Box>
        </Box>
        <DragOverlay modifiers={[restrictVerticalTransform]} zIndex={1300}>
          {dragPreview ? (
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1,
                boxShadow: 6,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                maxWidth: 280,
                pointerEvents: "none",
                cursor: "grabbing",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                component="div"
                sx={{ lineHeight: 1.2 }}
              >
                {dragPreview.kind === "period" ? "Période" : "Évènement"}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={dragPreview.name || undefined}
              >
                {dragPreview.name || "—"}
              </Typography>
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>
    </Box>
  );
});

export default ChronosTimelineView;
